import React, { Attributes, FC, PropsWithChildren, ReactNode, useEffect, useRef } from 'react'
import { Field, Form, FormSpy } from 'react-final-form'
import { has, isEqual } from 'lodash'
// eslint-disable-next-line import/no-named-as-default
import clsx from 'clsx'

import { FieldDependency, FormFieldV2, FormState } from '../../../types/interface/form.interface'
import { useCommonStyles } from '../../../styles/common_instabank'
import useFormConfig from '../../../hooks/shared/useFormConfig'
import RFFCustomSubmitBtn from './RFFCustomSubmitBtn'
import { Typography, RFFConditional } from '@dg-shared'
import { TranslatedTextType } from '../../../types'
import { FormApi } from 'final-form'
import LoadingSpinner from './MUICustomSpinner'

interface FormBuilderProps {
  id: string
  errorText?: TranslatedTextType
  validationArgs?: Record<string, unknown>
  externalDependency?: { [field: string]: boolean }
  customSubmit?: boolean
  initialValues?: Record<string, unknown>
  debug?: boolean
  // eslint-disable-next-line
  submitHandler(values?: any): void
  submitText?: TranslatedTextType
  submitClass?: string
  setFormState?(data: FormState<unknown>): void
  changeHandler?(values?: unknown): void
  // TODO: consider to make this prop a mandatory to avoid optional form defining confusing with formBuilder `optional`
  // prop and validation form JOI optional() configuration
  optional?: boolean
  isFormControllable?: boolean
  // eslint-disable-next-line
  filledFieldsData?: any
  customLabelFunc?(value: unknown): unknown
  handleBtnDisabled?(value: unknown): void
  classes?: string
  loadingSpinner?: boolean
  options?: Record<string, Record<string, unknown>[]>
}

/**
 * Pushes parent-owned values into the form and reports validity out of it, from an
 * effect rather than from the <Form> render prop.
 *
 * Both used to run during render, which is a setState-during-render violation
 * ("Cannot update a component while rendering a different component"). react-final-form
 * v6 tolerated it; v7 does not — the replay landed before the parent's `filledFieldsData`
 * mirror (fed by the FormSpy below) had caught up, so it wrote the stale value straight
 * back over what the user had just typed and the Verify page's email field could not be
 * filled at all. See DFE-3892.
 *
 * Only keys whose value changed *in the mirror* are pushed. That matters as much as the
 * move out of render: the mirror is the form's own output, so replaying all of it is
 * circular, and an effect that runs a commit late would otherwise reapply a stale
 * snapshot over newer input. Diffing against the last applied snapshot makes a late run
 * a no-op instead. Genuine parent-side pushes (useLogic's defineDate / defineCompany)
 * still come through, because those do change the mirror.
 */
const FormStateBridge: FC<{
  form: FormApi
  invalid: boolean
  // eslint-disable-next-line
  filledFieldsData?: any
  handleBtnDisabled?(value: unknown): void
}> = ({ form, invalid, filledFieldsData, handleBtnDisabled }) => {
  const applied = useRef<Record<string, unknown>>({})

  useEffect(() => {
    if (typeof filledFieldsData !== 'object' || !filledFieldsData) {
      return
    }

    const previous = applied.current
    applied.current = { ...filledFieldsData }

    Object.keys(filledFieldsData).forEach((name) => {
      if (!isEqual(previous[name], filledFieldsData[name])) {
        form.change(name, filledFieldsData[name])
      }
    })
  }, [form, filledFieldsData])

  useEffect(() => {
    !!handleBtnDisabled && handleBtnDisabled(invalid)
  }, [handleBtnDisabled, invalid])

  return null
}

const isDependencySatisfied = (
  { fieldName, value }: FieldDependency,
  values: Record<string, unknown>
): boolean =>
  typeof value === 'string'
    ? values[fieldName] === value
    : value.includes(values[fieldName] as string)

/**
 * A field whose dependency is not satisfied is not rendered, so the user cannot reach its
 * stored value — but it still has to leave the form as `null`. That matters for validation
 * as much as for the API payload: `owner` is `Joi.when('ownership', { otherwise:
 * Joi.valid(null) })`, so a leftover answer makes the whole form invalid and silently
 * disables the submit button with nothing on screen to explain it.
 *
 * Derived from the values here, at validate and submit, rather than written into the store
 * from RFFConditional's render as it used to be. That write was a setState-during-render
 * (the DFE-3892 class) and it made field visibility a second source of truth.
 */
const nullInactiveFields = (
  fields: Record<string, FormFieldV2>,
  values: Record<string, unknown>
): Record<string, unknown> => {
  const inactive = Object.values(fields).filter(
    (field) =>
      field.dependency && !field.dependency.every((dep) => isDependencySatisfied(dep, values))
  )

  if (!inactive.length) {
    return values
  }

  const nulled: Record<string, unknown> = { ...values }
  inactive.forEach((field) => {
    nulled[field.name] = null
  })

  return nulled
}

const FormBuilder: FC<PropsWithChildren<FormBuilderProps>> = ({
  id,
  errorText,
  validationArgs,
  externalDependency,
  initialValues,
  submitHandler,
  customSubmit,
  debug,
  submitClass,
  submitText,
  setFormState,
  changeHandler,
  optional,
  isFormControllable,
  customLabelFunc,
  handleBtnDisabled,
  filledFieldsData,
  classes,
  children,
  loadingSpinner,
  options,
}) => {
  const { fields, initialStaticValues, validation } = useFormConfig(id, validationArgs)
  const { classes: commonClasses } = useCommonStyles()

  if (!fields) {
    return null
  }

  return (
    <Form
      onSubmit={(values) => submitHandler(nullInactiveFields(fields, values))}
      validate={(values) => validation(nullInactiveFields(fields, values))}
      initialValues={initialValues || initialStaticValues}
      keepDirtyOnReinitialize={!!filledFieldsData}
    >
      {({ handleSubmit, values, submitting, pristine, valid, invalid, form }) => {
        pristine = optional ? false : pristine

        return (
          <form
            data-testid={`form-${id}`}
            onSubmit={handleSubmit}
            className={clsx(commonClasses.customForm, classes)}
            noValidate
            onChange={changeHandler}
            id={id}
          >
            {Object.keys(fields).map((key) => {
              const field = fields[key]
              const fieldOptions = options ? options[field.name] : null
              const fieldBody = (
                <React.Fragment key={field.name + key}>
                  <Field
                    name={field.name}
                    defaultValue={field.defaultValue}
                    initialValue={field.initialValue}
                    type={field.type}
                    format={field.format}
                    parse={field.parse}
                    formatOnBlur={field.formatOnBlur ?? true}
                    render={({ input, meta }) =>
                      React.createElement(field.component, {
                        input,
                        meta,
                        validationArgs,
                        type: field.type,
                        fieldLabel: !field.customLabel && field.label,
                        fieldPlaceholder: field.placeholder,
                        customLabel:
                          !!field.customLabel &&
                          React.createElement(
                            field.customLabel,
                            field.customLabelProps ||
                              ({
                                field,
                                invalid: (meta.error || meta.submitError) && !meta.pristine,
                                toggler: customLabelFunc,
                                value: input.value,
                              } as Attributes)
                          ),
                        labelPlacement: field.labelPlacement,
                        options: field.options,
                        fieldOptions,
                        errorMsg: field.errorMsg,
                        ...field.componentProps,
                      })
                    }
                  />
                  {!!errorText && (
                    <Typography color='textError' className={commonClasses.errorMessage}>
                      {errorText}
                    </Typography>
                  )}
                </React.Fragment>
              )

              if (field.dependency) {
                return field.dependency.reduceRight(
                  (prev: ReactNode, cur: FieldDependency) => (
                    <RFFConditional key={field.name} when={cur.fieldName} is={cur.value}>
                      {prev}
                    </RFFConditional>
                  ),
                  fieldBody
                )
              }

              if (has(externalDependency, [field.name])) {
                return externalDependency[field.name] ? fieldBody : null
              }

              return fieldBody
            })}
            {children}
            {!customSubmit &&
              (loadingSpinner === true ? (
                <LoadingSpinner />
              ) : (
                <RFFCustomSubmitBtn
                  {...{ submitting, pristine, valid, buttonText: submitText }}
                  classesCustom={submitClass ? submitClass : ''}
                  dataTestid={`form-${id}-submit-btn`}
                />
              ))}
            {debug && (
              <pre style={{ width: '300px', wordWrap: 'break-word' }}>
                {JSON.stringify(values, null, 2)}
              </pre>
            )}
            <FormStateBridge
              form={form}
              invalid={invalid}
              filledFieldsData={filledFieldsData}
              handleBtnDisabled={handleBtnDisabled}
            />
            {isFormControllable && (
              <FormSpy
                subscription={{ values: true, valid: true, pristine: true }}
                onChange={setFormState}
              />
            )}
          </form>
        )
      }}
    </Form>
  )
}

export default FormBuilder
